import React, {
  useEffect,
  useRef,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Card,
  Typography,
  Tooltip,
  useTheme,
  LinearProgress,
  IconButton,
  styled,
  Chip,
} from '@mui/material';
import DatasetIcon from '@mui/icons-material/Storage';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import HistoryIcon from '@mui/icons-material/History';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import DatasetTwoToneIcon from '@mui/icons-material/DatasetTwoTone';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import {
  handleCreatorRowClick,
  handleToggleFileDetailsPopup,
  handleToggleDeleteFilePopup,
  handleToggleFileDatasetPopup,
  handleModelRowClick,
} from '../../store/mlcreator-slice/mlcreator';
import { DownloadFile } from '../../services/FileServices';
import { EndPoints } from '../../axios/endPoints';
import MLCreator from './MLCreator';
import SelectedMLCreator from './SelectedMLCreator';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import MLPrediction from '../ml-prediction/MLPrediction';
import DeleteFilePopup from './DeleteFilePopup';
import CreatorFileDetails from './CreatorFileDetails';
import MLOptimization from '../ml-optimization/MLOptimization';
import PopoverTooltip from '../common/PopoverTooltip';
import { classificationRegressionTextColors } from '../../utils/useTextColors';
import CreatorFileData from './CreatorFileData';

interface DatasetRow {
  id: string;
  name: string;
  fileName: string;
  rowCount: number;
  columnCount: number;
  uploadDate: string;
  modelCount: number;
}

interface ModelRow {
  id: string;
  name: string;
  trainingDate: string;
  selectedModelTypeString: string;
  selectedModelSubtype: string;
  testingResultsCount: number;
}

interface StepDef {
  labelKey: string;
  defaultLabel: string;
  IconComponent: React.ComponentType;
}

const steps: StepDef[] = [
  {
    labelKey: 'CREATOR_STEPPER_DATASETS_LABEL',
    defaultLabel: 'Training Datasets',
    IconComponent: DatasetIcon,
  },
  {
    labelKey: 'CREATOR_STEPPER_SELECTED_DATASET_LABEL',
    defaultLabel: 'Dataset Models',
    IconComponent: ModelTrainingIcon,
  },
  {
    labelKey: 'CREATOR_STEPPER_TRAINING_RUNS_LABEL',
    defaultLabel: 'Training Runs',
    IconComponent: HistoryIcon,
  },
];

const StepCircle = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
  },
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  transition: 'all 0.3s ease',
}));

const stepMotion = {
  initial: { opacity: 0, y: 30, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -30, scale: 0.95 },
  transition: { duration: 0.4, ease: 'easeInOut' },
};

const DatasetTooltipContent: React.FC<{
  row: DatasetRow;
  onDataset: () => void;
  onDetails: () => void;
  onDownload: () => void;
  onTrain: () => void;
  onDelete: () => void;
}> = ({ row, onDataset, onDetails, onDownload, onTrain, onDelete }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 280,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: 3,
        border: '1px solid #e0e0e0',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {t('CREATOR_ID_FIELD')}: {row.id}
      </Typography>
      <Typography variant="body2">{t('CREATOR_NAME_FIELD')}: {row.name}</Typography>
      <Typography variant="body2">{t('CREATOR_FILE_NAME_FIELD')}: {row.fileName}</Typography>
      <Typography variant="body2">{t('CREATOR_ROW_COUNT_FIELD')}: {row.rowCount}</Typography>
      <Typography variant="body2">{t('CREATOR_COLUMN_COUNT_FIELD')}: {row.columnCount}</Typography>
      <Typography variant="body2">
        {t('CREATOR_UPLOAD_DATE_FIELD')}:{' '}
        {moment.utc(row.uploadDate).local().format('DD/MM/YYYY HH:mm')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('CREATOR_MODEL_COUNT_FIELD')}: {row.modelCount}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Tooltip title={t('CREATOR_DATA_ACTION')} arrow>
          <IconButton size="small" onClick={onDataset}>
            <DatasetTwoToneIcon className="dataset-icon" fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('CREATOR_DETAILS_ACTION')} arrow>
          <IconButton size="small" onClick={onDetails}>
            <InfoTwoToneIcon className="info-icon" fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('CREATOR_DOWNLOAD_ACTION')} arrow>
          <IconButton size="small" onClick={onDownload}>
            <CloudDownloadTwoToneIcon className="download-icon" fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('CREATOR_DELETE_ACTION')} arrow>
          <IconButton size="small" onClick={onDelete}>
            <DeleteForeverTwoToneIcon className="delete-icon" fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const ModelTooltipContent: React.FC<{ model: ModelRow }> = ({ model }) => {
  const { t } = useTranslation();
  const { color, backgroundColor } = classificationRegressionTextColors[model.selectedModelTypeString]

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 280,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: 3,
        border: '1px solid #e0e0e0',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold">
        {t('SELECTED_CREATOR_ID_FIELD')}: {model.id}
      </Typography>
      <Typography variant="body2">
        {t('SELECTED_CREATOR_MODEL_NAME_FIELD')}: {model.name}
      </Typography>
      <Typography variant="body2">
        {t('SELECTED_CREATOR_TRAINING_DATE_FIELD')}:{' '}
        {moment.utc(model.trainingDate).local().format('DD/MM/YYYY HH:mm')}
      </Typography>
      <Typography variant="body2">
        {t('SELECTED_CREATOR_MODEL_TYPE_FIELD')}: 
        &nbsp;
        <Chip
          label={t(model.selectedModelTypeString)}
          size="small"
          sx={{ color, backgroundColor, fontWeight: 'medium' }}
        />
      </Typography>
      <Typography variant="body2">
        {t('SELECTED_CREATOR_MODEL_SUBTYPE_FIELD')}: {t(model.selectedModelSubtype)}
      </Typography>
      <Typography variant="body2">
        {t('SELECTED_CREATOR_TESTING_RESULTS_FIELD')}: {model.testingResultsCount}
      </Typography>
    </Box>
  );
};

const MLCreatorSwitcher: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const creatorRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);

  const selectedDataset = useAppSelector(
    (s: RootState) => s.mlcreator.selectedCreatorRow
  ) as DatasetRow | null;
  const selectedModel = useAppSelector(
    (s: RootState) => s.mlcreator.selectedModelRow
  ) as ModelRow | null;

  const [activeStep, setActiveStep] = useState<number>(0);
  const [selectedPredictionOrOptimization, setSelectedPredictionOrOptimization] = useState<"prediction" | "optimization" | null>(null)

  const goToStep = useCallback(
    async (stepIndex: number, rowData?: DatasetRow) => {
      if (stepIndex === 0) {
        await dispatch(handleCreatorRowClick(null));
        setActiveStep(0);
      } else if (stepIndex === 1 && rowData) {
        await dispatch(handleCreatorRowClick(rowData));
        setActiveStep(1);
      }
      dispatch(handleModelRowClick(null));
    },
    [dispatch]
  );

  const onModelSelect = useCallback(
    (modelRow: ModelRow, type: "prediction" | "optimization") => {
      if (!selectedDataset) return;
      setSelectedPredictionOrOptimization(type)
      dispatch(handleModelRowClick(modelRow));
      setActiveStep(2);
    },
    [dispatch, selectedDataset]
  );

  useEffect(() => {
    if (activeStep === 1 && selectedDataset && creatorRef.current) {
      creatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStep, selectedDataset]);

  useEffect(() => {
    if (activeStep === 2 && selectedModel && modelRef.current) {
      modelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStep, selectedModel]);

  useEffect(() => {
    return () => {
      dispatch(handleCreatorRowClick(null));
      setActiveStep(0);
    };
  }, [dispatch]);

  const datasetTooltip = useMemo<ReactNode | null>(() => {
    if (!selectedDataset) return null;
    return (
      <DatasetTooltipContent
        row={selectedDataset}
        onDataset={() =>
          dispatch(
            handleToggleFileDatasetPopup({
              open: true,
              body: { datasetId: selectedDataset.id, pageNumber: 0, pageSize: 10}
            })
          )
        }
        onDetails={() =>
          dispatch(
            handleToggleFileDetailsPopup({
              open: true,
              body: { fileId: selectedDataset.id, fileName: selectedDataset.fileName },
            })
          )
        }
        onDownload={() =>
          DownloadFile(
            `${EndPoints.ML_CREATOR_DOWNLOAD_TRAINING_DATASET}?id=${selectedDataset.id}`
          )
        }
        onTrain={() => goToStep(1, selectedDataset)}
        onDelete={() =>
          dispatch(
            handleToggleDeleteFilePopup({
              open: true,
              body: {
                fileName: selectedDataset.fileName,
                fileId: selectedDataset.id,
              },
            })
          )
        }
      />
    );
  }, [selectedDataset, dispatch, goToStep]);

  const modelTooltip = useMemo<ReactNode | null>(() => {
    if (!selectedDataset || !selectedModel) return null;
    return <ModelTooltipContent model={selectedModel} />;
  }, [selectedDataset, selectedModel]);

  return (
    <>
      <Card
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 2, sm: 4 },
          backdropFilter: 'blur(4px)',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.6)'
              : 'rgba(255, 255, 255, 0.8)',
          boxShadow: theme.shadows[3],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 2, sm: 8 },
          }}
        >
          {steps.map((stepDef, idx) => {
            const isActive = activeStep === idx;
            const isCompleted = activeStep > idx;
            const canClick =
              idx === 0 || (idx === 1 && !!selectedDataset) || (idx === 2 && !!selectedModel);

            let tooltipContent: ReactNode | undefined;
            if (idx === 1 && selectedDataset) tooltipContent = datasetTooltip;
            if (idx === 2 && selectedDataset && selectedModel) tooltipContent = modelTooltip;

            return (
              <PopoverTooltip
                content={tooltipContent}
                placement="bottom"
              >
                <Box
                  onClick={() => canClick && (idx === 0 ? goToStep(0) : idx === 1 && selectedDataset && goToStep(1, selectedDataset))}
                  sx={{
                    cursor: canClick ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    transform: isActive ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                    opacity: isCompleted || isActive ? 1 : 0.5,
                  }}
                >
                  <StepCircle
                    sx={{
                      border: `2px solid ${isActive ? 'var(--button-background-color)' : theme.palette.grey[300]}`,
                      color: isActive ? 'var(--button-background-color)' : theme.palette.text.primary,
                      '&:hover': {
                        transform: canClick ? 'scale(1.15)' : 'none',
                        color: 'var(--button-background-color)',
                      },
                    }}
                  >
                    <stepDef.IconComponent />
                  </StepCircle>

                  <Typography
                    variant={isActive ? 'subtitle2' : 'caption'}
                    fontWeight={isActive ? 'bold' : 'normal'}
                    textAlign="center"
                    sx={{ color: isActive ? 'var(--button-background-color)' : undefined }}
                  >
                    {t(stepDef.labelKey, stepDef.defaultLabel)}
                  </Typography>
                </Box>
              </PopoverTooltip>
            );
          })}
        </Box>

        <Box sx={{ mt: 3 }}>
          <LinearProgress
            variant="determinate"
            value={(activeStep + 1) * (100 / steps.length)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'var(--button-background-color)',
              },
            }}
          />
        </Box>
      </Card>

      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', sm: 600 },
          borderRadius: { xs: 2, sm: 4 },
          overflow: 'hidden',
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          mt: 4,
          boxShadow: theme.shadows[4],
          backgroundColor: theme.palette.background.default,
        }}
      >
        <AnimatePresence mode="wait">
          {activeStep === 0 && (
            <motion.div key="step-0" {...stepMotion}>
              <MLCreator
                onSelectDataset={(row: DatasetRow) => {
                  goToStep(1, row);
                }}
              />
            </motion.div>
          )}

          {activeStep === 1 && selectedDataset && (
            <motion.div
              key="step-1"
              {...stepMotion}
              ref={creatorRef}
            >
              <SelectedMLCreator onSelectModel={onModelSelect} />
            </motion.div>
          )}

          {activeStep === 2 && selectedModel && (
            <motion.div key="step-2" {...stepMotion} ref={modelRef}>
              {
                selectedPredictionOrOptimization === "prediction" ? 
                <MLPrediction
                  modelId={selectedModel.id}
                  modelName={selectedModel.name}
                  showModelSelection={false}
                />
                : selectedPredictionOrOptimization === "optimization" ?
                <MLOptimization 
                  modelId={selectedModel.id}
                  modelName={selectedModel.name}
                  showModelSelection={false}
                /> :
                ''
              }
            </motion.div>
          )}
        </AnimatePresence>

        <DeleteFilePopup onDeleteSuccess={() => goToStep(0)} />
        <CreatorFileDetails />
        <CreatorFileData />
      </Box>
    </>
  );
};

export default MLCreatorSwitcher;